<div class="job-detail-card" [ngClass]="job.rank | rankClass">
  <!-- Rank Badge for Top 3 (Recommended Jobs) -->
  <div *ngIf="hasRankBadge()" class="rank-badge" [ngClass]="'rank-' + job.rank">
    <i *ngIf="job.rank === 1" class="fas fa-trophy"></i>
    <span>{{ job.rank | rankLabel }}</span>
  </div>

  <!-- Job Header with Logo, Title, and Action Section -->
  <div class="job-info">
    <a class="job-info__link" [routerLink]="jobDetailRoute" [queryParams]="jobQueryParams" [attr.aria-label]="jobDetailAriaLabel">
      <div class="job-details-main">
        <div class="main-details">
          <img *ngIf="job.tenantLogoUrl; else defaultLogo" [src]="job.tenantLogoUrl" [alt]="job.tenantName" class="company-logo" />
          <ng-template #defaultLogo>
            <div class="company-logo default-logo">
              {{ (job.tenantName || 'C').charAt(0).toUpperCase() }}
            </div>
          </ng-template>
          <div>
            <div class="job-title-row">
              <h3 class="job-title">{{ job.jobTitle || ('job.detail.unknownJob' | transloco) }}</h3>
              <span *ngIf="job.isApplied" class="applied-badge">
                <i class="fas fa-check-circle"></i>
                {{ 'job.applied' | transloco }}
              </span>
            </div>
            <div class="company-name">{{ job.tenantName || ('job.detail.unknownCompany' | transloco) }}</div>
          </div>
        </div>

        <div class="job-quick-details">
          <div class="detail-item" *ngIf="job.placeOfWork && job.placeOfWork.length > 0">
            <i class="fas fa-map-marker-alt"></i>
            <span>
              <ng-container *ngFor="let place of job.placeOfWork; let last = last">
                {{ 'jobWorkplaceTypes.' + place.toLowerCase() | transloco }}
                <ng-container *ngIf="!last">,</ng-container>
              </ng-container>
            </span>
          </div>
          <div class="detail-item" *ngIf="job.jobType && job.jobType.length > 0">
            <i class="fas fa-briefcase"></i>
            <span>
              <ng-container *ngFor="let type of job.jobType; let last = last">
                {{ 'jobEmploymentTypes.' + type | transloco }}
                <ng-container *ngIf="!last">,</ng-container>
              </ng-container>
            </span>
          </div>
          <div class="detail-item detail-item--salary" *ngIf="job.salary">
            <i class="fas fa-money-bill"></i>
            <span>
              {{ job.salary.currency | currencyDisplay }}{{ job.salary.basicSalary }}
              <span *ngIf="job.salary.upperSalary">– {{ job.salary.upperSalary }}</span>
              <span *ngIf="job.salary.period" class="salary-period-label">/ {{ 'jobSalaryPeriods.' + job.salary.period | transloco }}</span>
            </span>
          </div>
        </div>
      </div>
    </a>

    <div class="div">
      <div class="match-field" *ngIf="mode !== 'browse'">
        <div class="match-value" [ngClass]="job.matchScore | matchScoreClass">{{ job.matchScore | matchScoreNormalized }}%</div>
        <div class="match-label">{{ 'pages.jobs.recommendedJobs.matchScore' | transloco }}</div>
        <div class="progress-container">
          <div class="progress-bar" [style.width.%]="job.matchScore | matchScoreNormalized" [ngClass]="job.matchScore | matchScoreClass"></div>
        </div>
      </div>

      @if (mode === 'applied') {
      <!-- Application Status (Applied Jobs) -->
      <div class="match-field" *ngIf="job.applicationStatus">
        <div class="match-label">{{ 'job.applicationStatus' | transloco }}</div>
        <span class="status-badge" [ngClass]="job.applicationStatus.statusName | statusClass">
          <i class="fas {{ job.applicationStatus.statusName | statusIcon }}"></i>
          <span>{{ job.applicationStatus.statusName }}</span>
        </span>
      </div>
      } @else if (mode === 'saved') {
      <!-- Unsave Section (Saved Jobs) -->
      <app-button variant="filled" color="secondary" [disabled]="isUnsaving" (btnClick)="onUnsaveClick($event)">
        <mat-spinner *ngIf="isUnsaving" diameter="16"></mat-spinner>
        <i *ngIf="!isUnsaving" class="fas fa-check"></i>
        {{ 'job.saved' | transloco }}
      </app-button>
      } @else if (mode === 'browse') {
      <!-- Save/Unsave Section (Browse Jobs) — only for logged-in users -->
      <ng-container *ngIf="isLoggedIn">
        <app-button *ngIf="!job.isSaved" variant="outline" color="primary" [disabled]="isSaving" (btnClick)="onSaveClick($event)">
          <mat-spinner *ngIf="isSaving" diameter="16"></mat-spinner>
          <i *ngIf="!isSaving" class="fas fa-bookmark save-btn-icon"></i>
          {{ 'job.save' | transloco }}
        </app-button>
        <app-button *ngIf="job.isSaved" variant="filled" color="secondary" [disabled]="isUnsaving" (btnClick)="onUnsaveClick($event)">
          <mat-spinner *ngIf="isUnsaving" diameter="16"></mat-spinner>
          <i *ngIf="!isUnsaving" class="fas fa-check save-btn-icon"></i>
          {{ 'job.saved' | transloco }}
        </app-button>
      </ng-container>
      }
    </div>
  </div>

  <div class="job-info-footer">
    <div class="job-info-footer-details">
      <div class="detail-row" *ngIf="job.jobCategory">
        <span class="detail-label">{{ 'job.jobCategoryHeading' | transloco }}:</span>
        <span class="detail-value">{{ 'jobCategories.' + job.jobCategory.categoryKey | transloco }}</span>
      </div>
      <div class="detail-row" *ngIf="job.jobPosition">
        <span class="detail-label">{{ 'job.jobCategoryPositionHeading' | transloco }}:</span>
        <span class="detail-value">{{ 'jobPositions.' + job.jobPosition.positionKey | transloco }}</span>
      </div>

      @if (mode === 'applied') {
      <!-- Extended Job Details (Applied Jobs) -->
      <div class="detail-row" *ngIf="job.appliedDate">
        <span class="detail-label">{{ 'pages.jobs.appliedJobs.appliedOn' | transloco }}:</span>
        <span class="detail-value">{{ formatDateTime(job.appliedDate) }}</span>
      </div>
      <div class="detail-row" *ngIf="job.assignedBy">
        <span class="detail-label">{{ 'job.assignedBy' | transloco }}:</span>
        <span class="detail-value">{{ job.assignedBy.firstName }} {{ job.assignedBy.lastName }}</span>
      </div>
      } @else if (mode === 'saved') {
      <!-- Extended Job Details (Saved Jobs) -->
      <div class="detail-row" *ngIf="job.postedAt">
        <span class="detail-label">{{ 'job.posted' | transloco }}:</span>
        <span class="detail-value">{{ formatDateTime(job.postedAt) }}</span>
      </div>
      <!-- <div class="detail-row" *ngIf="job.experience">
          <span class="detail-label">{{ 'job.experience' | transloco }}:</span>
          <span class="detail-value">{{ job.experience }}</span>
        </div> -->
      <div class="detail-row" *ngIf="job.startDateAsap">
        <span class="detail-label">{{ 'pages.jobs.recommendedJobs.startDate' | transloco }}:</span>
        <span class="detail-value">{{ 'pages.jobs.recommendedJobs.asap' | transloco }}</span>
      </div>
      <div class="detail-row" *ngIf="job.startDate">
        <span class="detail-label">{{ 'pages.jobs.recommendedJobs.startDate' | transloco }}:</span>
        <span class="detail-value">{{ formatDate(job.startDate) }}</span>
      </div>
      } @else if (mode === 'browse') {
      <!-- Extended Job Details (Browse Jobs) -->
      <div class="detail-row" *ngIf="job.postedAt">
        <span class="detail-label">{{ 'job.posted' | transloco }}:</span>
        <span class="detail-value">{{ formatDate(job.postedAt) }}</span>
      </div>
      <div class="detail-row" *ngIf="job.startDateAsap">
        <span class="detail-label">{{ 'pages.jobs.recommendedJobs.startDate' | transloco }}:</span>
        <span class="detail-value">{{ 'pages.jobs.recommendedJobs.asap' | transloco }}</span>
      </div>
      <div class="detail-row" *ngIf="job.startDate">
        <span class="detail-label">{{ 'pages.jobs.recommendedJobs.startDate' | transloco }}:</span>
        <span class="detail-value">{{ formatDate(job.startDate) }}</span>
      </div>
      } @else if (mode === 'recommended') {
      <!-- Extended Job Details (Recommended Jobs) -->
      <div class="detail-row">
        <span class="detail-label">{{ 'pages.jobs.recommendedJobs.status' | transloco }}:</span>
        <span class="detail-value status-active">{{ job.status }}</span>
      </div>
      <div class="detail-row" *ngIf="job.startDateAsap">
        <span class="detail-label">{{ 'pages.jobs.recommendedJobs.startDate' | transloco }}:</span>
        <span class="detail-value">{{ 'pages.jobs.recommendedJobs.asap' | transloco }}</span>
      </div>
      <div class="detail-row" *ngIf="job.startDate">
        <span class="detail-label">{{ 'pages.jobs.recommendedJobs.startDate' | transloco }}:</span>
        <span class="detail-value">{{ formatDate(job.startDate) }}</span>
      </div>
      }
    </div>
  </div>
</div>
